import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import JoditEditor from "jodit-react";

import "jodit/es2021/jodit.min.css";
import "jodit/esm/plugins/paste-from-word/paste-from-word";
import "jodit/esm/plugins/table/table";
import "jodit/esm/plugins/image/image";
import "jodit/esm/plugins/image-properties/image-properties";
import "jodit/esm/plugins/resizer/resizer";
import "jodit/esm/plugins/drag-and-drop/drag-and-drop";
import "jodit/esm/plugins/inline-popup/inline-popup";
import "./WordRichTextEditor.css";

const URDU_FONT_STACK =
  '"NotoNastaliqUrdu", "Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", serif';
const DEFAULT_FONT_STACK = '"Segoe UI", Roboto, sans-serif';
const BLOCK_SELECTOR =
  "p,div,li,h1,h2,h3,h4,h5,h6,td,th,blockquote,figcaption";

export interface WordRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  className?: string;
}

export interface WordRichTextEditorHandle {
  getHtml: () => string;
}

const WordRichTextEditor = forwardRef<
  WordRichTextEditorHandle,
  WordRichTextEditorProps
>(
  (
    {
      value,
      onChange,
      placeholder = "Load text already saved in templates, or copy paste from external source",
      minHeight = 260,
      className,
    },
    ref,
  ) => {
    const valueRef = useRef(value);
    const lastSyncedRef = useRef(value);
    const editorInstanceRef = useRef<any>(null);
    const onChangeRef = useRef(onChange);

    valueRef.current = value;
    onChangeRef.current = onChange;

    const readEditorHtml = useCallback((): string => {
      const editor = editorInstanceRef.current;
      if (!editor) return lastSyncedRef.current ?? valueRef.current ?? "";

      const fromEditor =
        (typeof editor.getEditorValue === "function"
          ? editor.getEditorValue()
          : editor.value) ?? "";
      const fromSync = lastSyncedRef.current ?? "";

      if (!fromEditor) return fromSync;
      if (!fromSync) return fromEditor;
      return fromSync.length >= fromEditor.length ? fromSync : fromEditor;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getHtml: readEditorHtml,
      }),
      [readEditorHtml],
    );

    useEffect(() => {
      const editor = editorInstanceRef.current;
      if (!editor) return;

      if (value !== lastSyncedRef.current) {
        editor.value = value;
        lastSyncedRef.current = value;
      }
    }, [value]);

    const handleEditorChange = useCallback((newContent: string) => {
      lastSyncedRef.current = newContent;
      onChangeRef.current(newContent);
    }, []);

    const config = useMemo(
      () => ({
        readonly: false,
        placeholder,
        height: minHeight,
        minHeight,
        toolbarAdaptive: false,
        toolbarSticky: false,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: "insert_as_html" as const,
        processPasteFromWord: true,
        beautifyHTML: false,
        enableDragAndDropFileToEditor: true,
        toolbarInline: false,
        toolbarInlineForSelection: false,
        allowResizeTags: new Set(["img", "table"]),
        uploader: {
          insertImageAsBase64URI: true,
        },
        resizer: {
          showSize: true,
          forImageChangeAttributes: true,
          min_width: 40,
          min_height: 40,
          useAspectRatio: new Set(["img"]),
        },
        image: {
          openOnDblClick: true,
          editAlign: true,
          editSize: true,
          editMargins: true,
          editStyle: true,
          showPreview: true,
          useImageEditor: false,
        },
        enter: "p" as const,
        buttons: [
          "source",
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "|",
          "font",
          "fontsize",
          "lineHeight",
          "brush",
          "paragraph",
          "|",
          "align",
          "urdu",
          "|",
          "ul",
          "ol",
          "|",
          "outdent",
          "indent",
          "|",
          "table",
          "link",
          "image",
          "|",
          "hr",
          "eraser",
          "fullsize",
        ],
        removeButtons: [],
        disablePlugins: ["add-new-line"],
        style: {
          fontSize: "13px",
          fontFamily: DEFAULT_FONT_STACK,
        },
        extraButtons: [
          {
            name: "lineHeight",
            icon: "line-height",
            text: "Line Height",
            tooltip: "Line Height / Spacing",
            list: {
              "1.0": "1.0 (Single)",
              "1.15": "1.15",
              "1.5": "1.5 (1-1/2)",
              "2.0": "2.0 (Double)",
              "2.5": "2.5",
              "3.0": "3.0",
            },
            exec: (editor: any, current: any, control: any) => {
              try {
                const value = control.args?.[0];
                if (!value) return;

                editor.selection.focus();

                const root: HTMLElement = editor.editor;
                const blocks = new Set<HTMLElement>();

                editor.selection.eachSelection((current: Node) => {
                  const el =
                    current.nodeType === 1
                      ? (current as HTMLElement)
                      : current.parentElement;
                  const block = el?.closest(BLOCK_SELECTOR) as HTMLElement | null;
                  if (block && root.contains(block)) blocks.add(block);
                });

                if (blocks.size === 0) {
                  const currentSel = editor.selection.current();
                  const el = currentSel?.nodeType === 1 ? currentSel : currentSel?.parentElement;
                  const block = el?.closest(BLOCK_SELECTOR) as HTMLElement | null;
                  if (block && root.contains(block)) {
                    blocks.add(block);
                  } else {
                    blocks.add(root);
                  }
                }

                blocks.forEach((block) => {
                  block.style.lineHeight = value;
                });

                editor.events.fire("change", editor.value);
              } catch (e) {
                console.error("line height change failed", e);
              }
            },
          },
          {
            name: "urdu",
            text: "اردو",
            tooltip: "Toggle Urdu (right-to-left) direction for the selected paragraph(s)",
            exec: (editor: any) => {
              try {
                editor.selection.focus();

                const root: HTMLElement = editor.editor;
                const blocks = new Set<HTMLElement>();

                editor.selection.eachSelection((current: Node) => {
                  const el =
                    current.nodeType === 1
                      ? (current as HTMLElement)
                      : current.parentElement;
                  const block = el?.closest(BLOCK_SELECTOR) as HTMLElement | null;
                  if (block && root.contains(block)) blocks.add(block);
                });

                if (blocks.size === 0) blocks.add(root);

                const turningOn =
                  Array.from(blocks)[0].style.direction !== "rtl";

                blocks.forEach((block) => {
                  block.style.direction = turningOn ? "rtl" : "";
                  block.style.textAlign = turningOn ? "right" : "";
                  block.style.fontFamily = turningOn
                    ? URDU_FONT_STACK
                    : "";
                  if (turningOn) block.lang = "ur";
                  else block.removeAttribute("lang");
                });

                editor.events.fire("change", editor.value);
              } catch (e) {
                console.error("urdu toggle failed", e);
              }
            },
            isActive: (editor: any) => {
              const current = editor.selection.current();
              const el =
                current?.nodeType === 1
                  ? (current as HTMLElement)
                  : current?.parentElement;
              const block = el?.closest(BLOCK_SELECTOR) as HTMLElement | null;
              return block?.style.direction === "rtl" || false;
            },
          },
        ],
        events: {
          afterInit(editor: any) {
            editorInstanceRef.current = editor;
            const initial = valueRef.current || "";
            editor.value = initial;
            lastSyncedRef.current = initial;
          },
        },
      }),
      [minHeight, placeholder],
    );

    return (
      <div className={`word-rich-text-editor ${className || ""}`.trim()}>
        <JoditEditor config={config} onChange={handleEditorChange} />
      </div>
    );
  },
);

WordRichTextEditor.displayName = "WordRichTextEditor";

export default WordRichTextEditor;
